import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import Tooltip from 'components/Tooltip';
// Imported directly, not through the components/Inputs barrel: the barrel pulls
// in MetricPicker, which adds an import cycle (see AiReadyEditor).
import Dropdown from 'components/Inputs/Dropdown';
import TextArea from 'components/Inputs/TextArea';
import useMetricOptions from 'hooks/useMetricOptions';

import { AgentEvalContext } from './AgentEvalProvider';
import {
  ALL_CONCEPTS,
  conceptLabel,
  PRODUCT_ORDER,
  productLabel,
} from './constants';
import ConceptTooltip from './ConceptTooltip';

// Author or edit a check. Assertions are OPTIONAL: with none, the run is
// self-scored (did it ground, answer or clarify, and chart). A check that runs
// on every customer cannot name a metric id, because ids are account-specific —
// the metric fields only appear once the scope is this account.

const EditDiv = styled.div`
  padding: 20px 32px 40px 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 760px;
  overflow-y: auto;
  flex: 1;
`;

type Ending = 'either' | 'ask' | 'noAsk';

const ENDING_OPTIONS: { value: Ending; label: string }[] = [
  { value: 'either', label: 'Either is fine' },
  { value: 'noAsk', label: 'Must answer' },
  { value: 'ask', label: 'Must ask to clarify' },
];

const endingOf = (expected: AgentEval.Expectation): Ending => {
  if (expected.mustEndWithQuestion) {
    return 'ask';
  }
  if (expected.mustNotEndWithQuestion) {
    return 'noAsk';
  }
  return 'either';
};

const toggle = <T,>(list: T[], value: T): T[] =>
  list.includes(value)
    ? list.filter((entry) => entry !== value)
    : [...list, value];

const CheckEditor = ({
  check,
  onDone,
}: {
  check?: AgentEval.ListedCheck;
  onDone: (saved?: AgentEval.Check) => void;
}) => {
  const { account, saveCheck, runOne } = useContext(AgentEvalContext);
  const { metricOptionsNoSpecials, metricOptionsLookup } = useMetricOptions();

  const [question, setQuestion] = useState<string>(check?.question ?? '');
  const [purpose, setPurpose] = useState<string>(check?.purpose ?? '');
  const [scope, setScope] = useState<AgentEval.Scope['kind']>(
    check?.scope.kind ?? 'global',
  );
  const [products, setProducts] = useState<AgentEval.ProductKey[]>(
    check?.products ?? [],
  );
  const [requires, setRequires] = useState<AgentEval.DatasetConcept[]>(
    check?.requires ?? [],
  );
  const [ambiguous, setAmbiguous] = useState<boolean>(
    check?.ambiguous ?? false,
  );
  const [expectsChart, setExpectsChart] = useState<boolean>(
    check?.expectsChart ?? false,
  );
  const [metricId, setMetricId] = useState<string | undefined>(
    check?.expected.metricId,
  );
  const [forbidden, setForbidden] = useState<string[]>(
    check?.expected.forbiddenMetricIds ?? [],
  );
  const [ending, setEnding] = useState<Ending>(endingOf(check?.expected ?? {}));
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const metricOptions = useMemo(
    () =>
      metricOptionsNoSpecials.map((metric) => ({
        label: metric.name,
        value: metric.id,
        isSelected: metric.id === metricId,
        onSelected: () => setMetricId(metric.id),
      })),
    [metricOptionsNoSpecials, metricId],
  );
  const forbiddenOptions = useMemo(
    () =>
      metricOptionsNoSpecials.map((metric) => ({
        label: metric.name,
        value: metric.id,
        isSelected: forbidden.includes(metric.id),
        onSelected: () => setForbidden((c) => toggle(c, metric.id)),
      })),
    [metricOptionsNoSpecials, forbidden],
  );

  const blockingReason = (): string | undefined => {
    if (!question.trim()) {
      return 'A question is required';
    }
    if (products.length === 0) {
      return 'Pick at least one product';
    }
    if (scope === 'global' && metricId) {
      return 'A check that runs on every customer cannot expect a metric id';
    }
    return undefined;
  };
  const blocked = blockingReason();

  // Start from the stored assertions so the ones this form doesn't edit (a
  // seed's citation, routing and data assertions) survive a save; the form then
  // owns only the metric and ending keys.
  const buildExpectation = (): AgentEval.Expectation => {
    const {
      metricId: _metricId,
      forbiddenMetricIds: _forbidden,
      mustEndWithQuestion: _ask,
      mustNotEndWithQuestion: _noAsk,
      ...kept
    }: AgentEval.Expectation = check?.expected ?? {};
    const expected: AgentEval.Expectation = kept;
    if (scope === 'account' && metricId) {
      expected.metricId = metricId;
      if (forbidden.length > 0) {
        expected.forbiddenMetricIds = forbidden;
      }
    }
    if (ending === 'ask') {
      expected.mustEndWithQuestion = true;
    } else if (ending === 'noAsk') {
      expected.mustNotEndWithQuestion = true;
    }
    return expected;
  };

  const save = async (run: boolean) => {
    if (blocked) {
      return;
    }
    setIsSaving(true);
    const saved = await saveCheck({
      checkId: check?.id,
      question: question.trim(),
      purpose: purpose.trim(),
      scope,
      products,
      requires,
      expectsChart,
      // Only products that can compete make a check ambiguous.
      ambiguous: ambiguous && products.length >= 2,
      expected: buildExpectation(),
      reportId: check?.reportId ?? null,
      context: check?.context ?? null,
    });
    if (saved && run) {
      await runOne({ checkId: saved.id, target: 'resolved' });
    }
    setIsSaving(false);
    if (saved) {
      onDone(saved);
    }
  };

  return (
    <EditDiv data-testid="agent-eval-editor">
      <Typography.Header type="H4">
        {check ? 'Edit check' : 'New evaluation check'}
      </Typography.Header>

      <Typography.Body type="Label">Question</Typography.Body>
      <TextArea
        value={question}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setQuestion(e.target.value)
        }
        placeholder="What were our loaded miles per driver last month?"
        data-testid="agent-eval-question"
      />

      <Typography.Body type="Label">Purpose</Typography.Body>
      <TextArea
        value={purpose}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setPurpose(e.target.value)
        }
        placeholder="What a reader should look for in the answer"
        data-testid="agent-eval-purpose"
      />

      <Typography.Body type="Label">Products</Typography.Body>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        Where the question belongs. The run passes when the agent reads one of
        these, and an account without any of them never sees this check.
      </Typography.Body>
      <Row centerAlign style={{ gap: 6, flexWrap: 'wrap' }}>
        {PRODUCT_ORDER.map((product) => (
          <Button
            key={product}
            type={products.includes(product) ? 'Selected' : 'Secondary'}
            size="Small"
            label={productLabel(product)}
            testId={`agent-eval-editor-product-${product}`}
            onClick={() => setProducts((c) => toggle(c, product))}
          />
        ))}
      </Row>
      {products.length >= 2 && (
        <Row centerAlign style={{ gap: 8 }}>
          <Button
            type={ambiguous ? 'Selected' : 'Secondary'}
            size="Small"
            label="Ambiguous"
            testId="agent-eval-editor-ambiguous"
            onClick={() => setAmbiguous(!ambiguous)}
          />
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            {ambiguous
              ? 'These compete. With two or more of them on the account, it must ask which; with one, it must go there.'
              : 'Any of these is a correct place to answer from.'}
          </Typography.Body>
        </Row>
      )}

      <Typography.Body type="Label">Data it needs</Typography.Body>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        Named by concept, so one check works whatever the account&apos;s source
        system calls the dataset. Nothing selected means it runs anywhere.
      </Typography.Body>
      <Row centerAlign style={{ gap: 6, flexWrap: 'wrap' }}>
        {ALL_CONCEPTS.map((concept) => (
          <Tooltip
            key={concept}
            content={
              <ConceptTooltip
                concept={concept}
                accountDatasetTypes={account.datasetTypes}
              />
            }
          >
            <Button
              type={requires.includes(concept) ? 'Selected' : 'Secondary'}
              size="Small"
              label={conceptLabel(concept)}
              testId={`agent-eval-editor-concept-${concept}`}
              onClick={() => setRequires((c) => toggle(c, concept))}
            />
          </Tooltip>
        ))}
      </Row>

      <Typography.Body type="Label">Scope</Typography.Body>
      <Row centerAlign style={{ gap: 6 }}>
        <Button
          type={scope === 'global' ? 'Selected' : 'Secondary'}
          size="Small"
          label="All customers"
          testId="agent-eval-editor-scope-global"
          onClick={() => setScope('global')}
        />
        <Button
          type={scope === 'account' ? 'Selected' : 'Secondary'}
          size="Small"
          label="This account only"
          testId="agent-eval-editor-scope-account"
          onClick={() => setScope('account')}
        />
      </Row>

      <Typography.Body type="Label">A good answer</Typography.Body>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        Advisory: reported with each run, but routing decides the verdict.
      </Typography.Body>
      <Row centerAlign style={{ gap: 6, flexWrap: 'wrap' }}>
        <Button
          type={expectsChart ? 'Selected' : 'Secondary'}
          size="Small"
          label="Draws a chart"
          testId="agent-eval-editor-chart"
          onClick={() => setExpectsChart(!expectsChart)}
        />
        {ENDING_OPTIONS.map((option) => (
          <Button
            key={option.value}
            type={ending === option.value ? 'Selected' : 'Secondary'}
            size="Small"
            label={option.label}
            testId={`agent-eval-editor-ending-${option.value}`}
            onClick={() => setEnding(option.value)}
          />
        ))}
      </Row>

      {scope === 'account' && (
        <>
          <Typography.Body type="Label">Expected metric</Typography.Body>
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            Optional. Without it the run is self-scored; with it, the run also
            asserts the agent grounded on this metric and not its twin.
          </Typography.Body>
          <Dropdown
            options={metricOptions}
            selectedLabel={
              metricId ? metricOptionsLookup[metricId]?.name ?? metricId : ''
            }
            placeholder="The metric the agent should ground on"
            testId="agent-eval-editor-metric"
          />
          <Typography.Body type="Label">Forbidden metrics</Typography.Body>
          <Dropdown
            isMulti
            options={forbiddenOptions}
            selectedLabel={forbidden
              .map((id) => metricOptionsLookup[id]?.name ?? id)
              .join(', ')}
            placeholder="Metrics the agent must NOT ground on"
            testId="agent-eval-editor-forbidden"
          />
        </>
      )}

      <Row centerAlign spaceBetween style={{ marginTop: 8 }}>
        <Button
          type="Ghost"
          size="Small"
          label="Cancel"
          onClick={() => onDone()}
        />
        <Row centerAlign style={{ gap: 8 }}>
          <Button
            type="Secondary"
            size="Small"
            label="Save"
            isLoading={isSaving}
            isDisabled={Boolean(blocked)}
            isDisabledMessage={blocked}
            testId="agent-eval-editor-save"
            onClick={() => save(false)}
          />
          <Button
            type="Primary"
            size="Small"
            label="Save and run"
            isLoading={isSaving}
            isDisabled={Boolean(blocked)}
            isDisabledMessage={blocked}
            testId="agent-eval-editor-save-and-run"
            onClick={() => save(true)}
          />
        </Row>
      </Row>
    </EditDiv>
  );
};

export default CheckEditor;
